import React, { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface EmailPreferences {
  marketing: boolean;
  productUpdates: boolean;
  tips: boolean;
  unsubscribed: boolean;
}

interface EmailPreferencesSectionProps {
  userEmail: string;
}

const EmailPreferencesSection: React.FC<EmailPreferencesSectionProps> = ({ userEmail }) => {
  const [preferences, setPreferences] = useState<EmailPreferences>({
    marketing: true,
    productUpdates: true,
    tips: true,
    unsubscribed: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchEmailPreferences();
  }, []);

  const fetchEmailPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/email-preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences({
          marketing: data.marketing ?? true,
          productUpdates: data.productUpdates ?? true,
          tips: data.tips ?? true,
          unsubscribed: data.unsubscribed ?? false
        });
      }
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEmailPreferences = async (newPreferences: EmailPreferences) => {
    setSaving(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/email-preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPreferences)
      });

      if (response.ok) {
        setPreferences(newPreferences);
        setMessage('Email preferences updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to update preferences');
      }
    } catch (error) {
      setMessage('Failed to update email preferences. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceChange = (key: keyof EmailPreferences, value: boolean) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);
    updateEmailPreferences(newPreferences);
  };

  const handleUnsubscribeAll = () => {
    const newPreferences = {
      marketing: false,
      productUpdates: false,
      tips: false,
      unsubscribed: true
    };
    setPreferences(newPreferences);
    updateEmailPreferences(newPreferences);
  };

  const handleResubscribe = () => {
    const newPreferences = {
      marketing: true,
      productUpdates: true,
      tips: true,
      unsubscribed: false
    };
    setPreferences(newPreferences);
    updateEmailPreferences(newPreferences);
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Email Preferences</h3>
        <div className="text-center py-4 text-gray-600">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Email Preferences</h3>
      <p className="text-sm text-gray-600 mb-6">
        Choose which emails you'd like to receive from DoseWise. You can change these settings anytime.
      </p>

      {preferences.unsubscribed ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.73 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">You are currently unsubscribed from all emails</p>
              <p className="text-yellow-600 text-sm">You won't receive any updates, tips, or important notifications.</p>
            </div>
          </div>
          <button
            onClick={handleResubscribe}
            disabled={saving}
            className="mt-3 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-400"
          >
            {saving ? 'Updating...' : 'Resubscribe to All'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Product Updates</h4>
              <p className="text-sm text-gray-600">New features, improvements, and important announcements</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.productUpdates}
                onChange={(e) => handlePreferenceChange('productUpdates', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Weekly Tips</h4>
              <p className="text-sm text-gray-600">Helpful tips for Mounjaro management and cost savings</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.tips}
                onChange={(e) => handlePreferenceChange('tips', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Community Updates</h4>
              <p className="text-sm text-gray-600">User success stories and community highlights</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-6">
            <button
              onClick={handleUnsubscribeAll}
              disabled={saving}
              className="text-red-600 hover:text-red-800 text-sm font-medium disabled:text-gray-400"
            >
              {saving ? 'Updating...' : 'Unsubscribe from all emails'}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          message.includes('successfully') 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Email: {userEmail} • We respect your privacy and will never share your email address.
        </p>
      </div>
    </div>
  );
};

export default EmailPreferencesSection; 